import { useEffect, useRef, useState } from 'react';
import braintree from 'braintree-web';
import { PaymentConfig } from '@v2/shared/lib/types';
import createBraintreeSession from '../createBraintreeSession';
import { REVEALED, WalletReveal } from '../useWalletReveal';
import { SubmitPayload, WalletShape } from '../types';

type Props = {
  payment: PaymentConfig;
  /**
   * What is being authorized, as one number.
   *
   * PayPal is the odd one of the three: it collects the address on its own site and
   * hands back an approval, so there is no sheet here to reprice and nothing to tell
   * about shipping or tax. It only needs the amount, and only at the moment of the
   * click — which is why this is a plain number where the other two take a `PaymentCart`.
   */
  amount: number;
  submitPayload: SubmitPayload;
  /** This button's gate in the row it sits in — see the same prop on GooglePay. */
  reveal?: WalletReveal;
  /** The corner the row is drawn with — see `WalletShape`. */
  shape?: WalletShape;
};

/**
 * The row's shape, as PayPal's sdk takes it: one of its own three names. `rect` is the 4px
 * corner `rounded` means, and is what the legacy cart's button already draws.
 */
const BUTTON_SHAPE: Record<WalletShape, 'pill' | 'rect'> = { pill: 'pill', rounded: 'rect' };

/**
 * How long the PayPal SDK is given to load before this button gives up on it.
 *
 * `loadPayPalSDK` inserts a script from paypal.com and resolves when it runs. A script
 * that is *blocked* never runs, and the promise then neither resolves nor rejects — the
 * same distinction `DEVICE_DATA_TIMEOUT` exists for in createBraintreeSession, and the
 * same one that took wallet buttons off the page in #1770.
 *
 * It is not hypothetical here. The site's consent manager replaces
 * `document.createElement` and blocks paypal.com for any visitor who declines the
 * functional or performance categories — which is everyone who uses the "Do Not Sell or
 * Share My Personal Information" link, and everyone auto-opted-out by a GPC signal. Their
 * PayPal button cannot appear, and without this it does not fail either: it waits
 * forever, reports nothing, and says nothing to the console. The legacy cart has the same
 * hole and hides it, because its buttons are independent and nothing waits on the one
 * that hangs.
 *
 * So this does not make PayPal work for those visitors — nothing here can, the script is
 * blocked. It makes the failure land: the setup rejects, the row is told this wallet is
 * done, and the console says why the next time somebody goes looking.
 */
const SDK_LOAD_TIMEOUT = 10000;

/**
 * PayPal, as an express checkout, and the third of the wallet row.
 *
 * Shared and configured from `payment` like its two siblings, and like them it renders
 * nothing at all until the gateway has answered — a failure here leaves the ordinary
 * Checkout button beside it, which is already on the page.
 *
 * What it does *not* share is the sheet. Apple and Google open a modal we own and ask us
 * to reprice as the visitor picks an address, so those two carry a whole `PaymentCart`
 * and three or four callbacks. PayPal opens its own window on its own site, collects the
 * address there and returns an approval, so the entire conversation is: here is an
 * amount, here is a nonce. That is why this is much the shorter component.
 *
 * The amount is read from a ref at click time rather than closed over. PayPal's SDK
 * takes `createBillingAgreement` once, when the button is rendered, and the drawer this
 * sits in changes underneath it every time a line is added or removed — so a closed-over
 * amount would authorize whatever the cart came to when the button was first drawn. The
 * ref is what lets it be drawn once and still be right, which is the same arrangement
 * ApplePay and GooglePay use for the same reason.
 *
 * Vault or capture is the gateway's call, not ours: a site that configures a PayPal
 * descriptor gets a billing agreement (`vault` / `createBillingAgreement`), one that does
 * not gets a single charge (`checkout` / `createOrder`). That is the branch
 * `src/DMagzWeb/shared/components/paypal/base_button.js` has always taken, keyed on the
 * same value, and it is deliberately not a decision made here.
 */
export default function PayPal({
  payment,
  amount,
  submitPayload,
  reveal = REVEALED,
  shape = 'pill',
}: Props) {
  const [isReady, setIsReady] = useState(false);
  /** That the question has been answered, either way — see the same state in ApplePay. */
  const [hasSettled, setHasSettled] = useState(false);
  const [paypalInstance, setPaypalInstance] = useState<any>(null);
  /** The page's device data collection, still running — see the same ref in ApplePay. */
  const deviceData = useRef<Promise<string | null> | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const amountRef = useRef(amount);

  const billingAgreementDescription = payment.paypal?.billingAgreementDescription || null;
  const isVaulted = !!billingAgreementDescription;

  useEffect(() => {
    amountRef.current = amount;
  }, [amount]);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        const session = await createBraintreeSession(payment.clientToken);
        const instance: any = await braintree.paypalCheckout.create({ client: session.client });

        // Inserts PayPal's own script and resolves once `window.paypal` is usable. The
        // vault flag has to agree with the flow chosen below or the SDK loads a client
        // that cannot start the payment it is later asked for.
        await Promise.race([
          instance.loadPayPalSDK({
            intent: isVaulted ? 'tokenize' : 'capture',
            currency: 'USD',
            vault: isVaulted,
          }),
          new Promise((_resolve, reject) =>
            setTimeout(
              () => reject(new Error('The PayPal SDK did not load — it is usually blocked by a consent manager')),
              SDK_LOAD_TIMEOUT
            )
          ),
        ]);

        if (cancelled || !window.paypal) {
          return;
        }

        deviceData.current = session.deviceData;
        setPaypalInstance(instance);
        setIsReady(true);
      } catch (error) {
        // Console only: the Checkout button beside this one is the fallback.
        console.error('Could not initialize PayPal', error);
      } finally {
        // Including the `cancelled` exit above — the row cannot be left waiting on a run
        // that was abandoned.
        setHasSettled(true);
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
    // Once, for the life of the component: nothing built here depends on the amount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Reported once, from the state transition rather than from the setup. */
  useEffect(() => {
    if (hasSettled) {
      reveal.reportSettled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSettled]);

  useEffect(() => {
    if (!buttonRef.current || !paypalInstance) {
      return;
    }

    // Cleared first, so a re-run cannot leave two buttons stacked in the div.
    buttonRef.current.innerHTML = '';

    window.paypal
      .Buttons({
        fundingSource: window.paypal.FUNDING.PAYPAL,
        style: { label: 'pay', shape: BUTTON_SHAPE[shape], tagline: false, height: 42 },
        [isVaulted ? 'createBillingAgreement' : 'createOrder']: () =>
          paypalInstance.createPayment({
            flow: isVaulted ? 'vault' : 'checkout',
            billingAgreementDescription,
            intent: isVaulted ? 'tokenize' : 'capture',
            amount: amountRef.current,
            currency: 'USD',
            // PayPal collects the delivery address, which is the whole point of it being
            // an express checkout rather than a payment method chosen at step three.
            enableShippingAddress: true,
            displayName: payment.paypal?.businessName,
          }),
        onApprove: async (data: unknown) => {
          try {
            await submitPayload(JSON.stringify(await toPayload(paypalInstance, data, await deviceData.current)));
          } catch (error) {
            console.error('Could not complete the PayPal payment', error);
          }
        },
        // A visitor who closes PayPal's window is not an error and there is nothing to
        // say about it — the button is still there and still works.
        onCancel: () => {},
        onError: (error: unknown) => console.error('PayPal reported an error', error),
      })
      .render(buttonRef.current);
    // `isReady` is a dependency for the same reason it is one in GooglePay: it is what
    // puts the div on the page, so until it commits `buttonRef.current` is null and this
    // can only bail. `reveal.isRevealed` is the commit that does it in a shared row.
    //
    // The amount is deliberately absent: the button is drawn once and reads the amount
    // through `amountRef` when it is clicked. Redrawing it per cart change is what the
    // legacy sidebar does, and it costs a full `paypal.Buttons(...).render()` each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypalInstance, isReady, reveal.isRevealed]);

  if (!isReady || !reveal.isRevealed) {
    return null;
  }

  return <div ref={buttonRef} />;
}

/**
 * The approval, in the shape checkout stores an order in.
 *
 * The address copy is not tidying. `extractCustomerDetailsFromPaymentPayload` returns an
 * empty set of customer details unless the payload carries **both** a shipping and a
 * billing address, and it does so without marking the information incomplete — so a
 * payload with only the one PayPal actually returns puts the customer at step two of
 * checkout with an empty name and address and no explanation. PayPal gives us the
 * delivery address it collected and nothing to bill against, so the delivery address is
 * used for both, which is what the legacy button has always done.
 */
async function toPayload(instance: any, data: unknown, deviceData: string | null) {
  const payload = await instance.tokenizePayment(data);

  if (payload.details?.shippingAddress) {
    payload.details.shippingAddress.forename = payload.details.firstName;
    payload.details.shippingAddress.surname = payload.details.lastName;
    payload.details.billingAddress = payload.details.shippingAddress;
  }

  // Top level, not under `details`: public/gateways/braintree.php reads
  // `$payload->deviceData` off the root — see the same line in ApplePay.
  return { ...payload, deviceData };
}
